import { useState, useEffect } from 'react';
import { Eye, EyeOff, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { KordaroLogo, KordaroMark } from '../../core/shared/components/KordaroLogo.js';

export function ResetPasswordPage() {
  const [token, setToken] = useState('');
  // welkom=1: eerste login via uitnodigingsmail (zelfde token-flow als wachtwoord-reset)
  const [welkom, setWelkom] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token');
    setWelkom(params.get('welkom') === '1');
    if (t) setToken(t);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) { setError('Wachtwoord moet minstens 8 tekens bevatten'); return; }
    if (password !== passwordConfirm) { setError('Wachtwoorden komen niet overeen'); return; }
    if (!/[A-Z]/.test(password) || !/[0-9]/.test(password)) {
      setError('Wachtwoord moet minstens 1 hoofdletter en 1 cijfer bevatten'); return;
    }

    setLoading(true);
    try {
      const res = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Er is een fout opgetreden');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Ongeldige link</h2>
          <p className="text-gray-500 text-sm mb-6">Geen resettoken gevonden. Vraag een nieuwe resetlink aan.</p>
          <a href="/forgot-password" className="inline-block w-full py-2.5 px-4 bg-gold hover:bg-gold-dark text-white font-medium rounded-lg transition-colors text-center">
            Nieuw wachtwoord aanvragen
          </a>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{welkom ? 'Je account is klaar!' : 'Wachtwoord gewijzigd!'}</h2>
          <p className="text-gray-500 mb-6">{welkom ? 'Log in met je e-mailadres en je nieuwe wachtwoord.' : 'Je kan nu inloggen met je nieuwe wachtwoord.'}</p>
          <a href="/" className="inline-block w-full py-2.5 px-4 bg-gold hover:bg-gold-dark text-white font-medium rounded-lg transition-colors text-center">
            Naar inlogpagina
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4"><KordaroLogo width={200} /></div>
          <h1 className="text-2xl font-bold text-gray-900">{welkom ? 'Welkom!' : 'Nieuw wachtwoord'}</h1>
          <p className="text-gray-500 mt-1">{welkom ? 'Kies een wachtwoord om je account te activeren' : 'Kies een nieuw wachtwoord voor je account'}</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-card border border-card-border rounded-[14px] p-8 shadow-sm">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Nieuw wachtwoord</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full px-3 py-2 pr-16 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold"
                placeholder="Min. 8 tekens, 1 hoofdletter, 1 cijfer"
                required
                minLength={8}
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" tabIndex={-1}>
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">Wachtwoord bevestigen</label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={passwordConfirm}
              onChange={e => setPasswordConfirm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold"
              placeholder="Herhaal wachtwoord"
              required
              minLength={8}
            />
          </div>

          <button type="submit" disabled={loading}
            className="w-full py-2.5 px-4 bg-gold hover:bg-gold-dark text-white font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
            {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Opslaan...</> : 'Wachtwoord opslaan'}
          </button>
        </form>
      </div>
    </div>
  );
}
